import {
  debounce,
  TextField,
  FormControl,
  InputLabel,
  NativeSelect,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridFilterItem,
} from "@mui/x-data-grid-pro";
import React from "react";

function CustomFilterInputValue(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;
  const customFilterRef: React.Ref<any> = React.useRef(null);
  const [inputValue, setInputValue] = React.useState(item.value || 0);

  const handleSearchTextChange = React.useCallback(
    debounce((newValue: string) => {
      applyValue({ ...item, value: newValue });
    }, 1000),
    [applyValue, item],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const numericValue = parseFloat(newValue);
    if (numericValue < 0) {
      return;
    }
    setInputValue(newValue);
    handleSearchTextChange(newValue);
  };

  React.useEffect(() => {
    setInputValue(item.value || "");
  }, [item.value]);
  return (
    <TextField
      name="mfrFilterValue"
      onChange={handleInputChange}
      ref={customFilterRef}
      size="small"
      value={inputValue}
      variant="standard"
      label="Value"
      type="number"
      inputProps={{ min: 0 }}
      sx={{ pt: 0.4 }}
    ></TextField>
  );
}

function BooleanFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Value
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "boolean-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="true">Enabled</option>
        <option value="false">Disabled</option>
      </NativeSelect>
    </FormControl>
  );
}

function SyncTypeFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Sync Type
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "sync-type-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="true">Reverse</option>
        <option value="false">Regular</option>
      </NativeSelect>
    </FormControl>
  );
}

function MatchingTypeFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Matching Type
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "matching-type-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="Catalog">Catalog</option>
        <option value="DMS">DMS</option>
        <option value="Custom">Custom</option>
      </NativeSelect>
    </FormControl>
  );
}

export const customPriceFilterOperators: GridFilterOperator[] = [
  {
    label: "Equals",
    value: "equals",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
  {
    label: "Less than",
    value: "lessThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
  {
    label: "Greater than",
    value: "greaterThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
];

export const booleanFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        const itemValue = filterItem.value === "true";
        return params.value === itemValue;
      };
    },
    InputComponent: BooleanFilterInput,
  },
];

export const syncTypeFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        const itemValue = filterItem.value === "true";
        return params.value === itemValue;
      };
    },
    InputComponent: SyncTypeFilterInput,
  },
];

export const matchingTypeFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        return params.value === filterItem.value;
      };
    },
    InputComponent: MatchingTypeFilterInput,
  },
];
